import React, {useContext, useEffect, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import SortableTable, {TableData} from "@common/SortableTable";
import {EventConsumer} from "@/api/dto/dto";
import DisplayHelpers from "@/helpers/display_helpers";
import {TableLink} from "@common/TableLink.styles";
import {useSearchParams} from "react-router-dom";
import {SearchParam} from "@/helpers/search_params";
import EventConsumerDrawer from "@/components/pages/settings/event_consumers/EventConsumerDrawer";
import Icon from "patient-ping-remedy/packages/icon";
import {colors} from "patient-ping-remedy/packages/theme";
import Button from "patient-ping-remedy/packages/button";
import CreateEventConsumerModal from "@/components/pages/settings/event_consumers/CreateEventConsumerModal";

const tableHeaders = [
  "Name",
  "Target UUID",
  "Created at",
  "Updated at"
];

const EventConsumersTab = () => {
  const {carecoApi} = useContext(CarecoApiContext);
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [eventConsumerId, setEventConsumerId] = useState<Nullable<number>>(null);
  const eventConsumerSearchId = searchParams.get(SearchParam.EVENT_CONSUMER_ID);

  const {data: eventConsumers, isLoading, error} = useQuery({
    queryKey: ['eventConsumers'],
    queryFn: () => carecoApi?.getAllEventConsumers(),
    enabled: !!carecoApi
  });

  useEffect(() => {
    if(eventConsumers) {
      const mapEventConsumerToTableData = (eventConsumer: EventConsumer) : TableData => {
        return {
          name: {
            tableHeader: 'Name',
            key: 'name',
            type: 'string',
            value: eventConsumer.name,
            displayValue: <TableLink to={`/settings/event_consumers?${SearchParam.EVENT_CONSUMER_ID}=${eventConsumer.id}`}>
              {eventConsumer.name}
            </TableLink>,
            columnWidth: '20%'
          },
          uuid: {
            tableHeader: 'Target UUID',
            key: 'uuid',
            type: 'string',
            value: eventConsumer.consumerUuid || '',
            displayValue: eventConsumer.consumerUuid
          },
          createdAt: {
            tableHeader: 'Created at',
            key: 'createdAt',
            type: 'date',
            value: eventConsumer.createdAt!,
            displayValue: DisplayHelpers.formatDateTimeShort(eventConsumer.createdAt || ""),
          },
          updatedAt: {
            tableHeader: 'Updated at',
            key: 'updatedAt',
            type: 'date',
            value: eventConsumer.updatedAt!,
            displayValue: DisplayHelpers.formatDateTimeShort(eventConsumer.updatedAt || ""),
          },
        };
      };

      setTableData(eventConsumers.map((eventConsumer: EventConsumer) => {
        return mapEventConsumerToTableData(eventConsumer);
      }));
    }
  }, [eventConsumers]);

  useEffect(() => {
    if(eventConsumerSearchId){
      if(eventConsumers) {
        const eventConsumer = eventConsumers.find((eventConsumer => eventConsumer.id === Number(eventConsumerSearchId)));
        if (eventConsumer) {
          setEventConsumerId(Number(eventConsumer.id));
        } else {
          searchParams.delete(SearchParam.EVENT_CONSUMER_ID);
          setSearchParams(searchParams);
        }
      }
    } else {
      closeSlideInPane();
    }
  }, [eventConsumerSearchId, eventConsumers]);

  const closeSlideInPane = () => {
    if(searchParams.has(SearchParam.EVENT_CONSUMER_ID)) {
      searchParams.delete(SearchParam.EVENT_CONSUMER_ID);
      setSearchParams(searchParams);
    }
    setEventConsumerId(null);
  };

  return (
    <>
      <SortableTable
        id={"event_consumers"}
        loading={isLoading}
        loadError={error}
        data={tableData}
        header={tableHeaders}
        searchColumns={['name', 'uuid']}
        sortableColumns={tableData.length > 0 ? [
          tableData[0]['name'],
          tableData[0]['createdAt'],
          tableData[0]['updatedAt']
        ] : []}
        searchPlaceholder={"Search by name or uuid"}
        excludeMixpanel={true}
      >
        <Button onClick={() => setIsModalOpen(true)}>
          <Icon iconClass={'plus'} color={colors.white} style={{ marginRight: '10px' }} />
          Add Event Consumer
        </Button>
      </SortableTable>

      <EventConsumerDrawer
        isOpen={!!eventConsumerId}
        onClose={closeSlideInPane}
        eventConsumerId={eventConsumerId}
      />

      {isModalOpen &&
        <CreateEventConsumerModal close = {() => setIsModalOpen(false)}/>
      }
    </>
  );
};

export default EventConsumersTab;
